/**
 * Removal Gesture Controller
 *
 * Manages the tap-and-hold gesture state machine for creature removal on mobile.
 * Distinguishes between quick tap (reposition) and long hold (removal).
 */

import type { AxialCoordinate } from '@drawn-of-war/shared';

/**
 * Removal gesture phases
 */
export type RemovalPhase =
  | 'idle'           // No interaction
  | 'detecting'      // Pointer down, waiting for hold threshold (500ms)
  | 'removing'       // Hold threshold passed, showing removal UI
  | 'cancelled';     // User cancelled (moved or released early)

/**
 * Removal gesture state
 */
export interface RemovalState {
  phase: RemovalPhase;
  targetCreatureId: string | null;
  targetHex: AxialCoordinate | null;
  pointerStartPos: { x: number; y: number } | null;
  holdStartTime: number | null;
  removalProgress: number; // 0-1, for timer circle animation
}

/**
 * Configuration for gesture timing
 */
export interface RemovalGestureConfig {
  /** Time before removal sequence starts (ms) */
  detectionThreshold: number;
  /** Time for removal timer to complete (ms) */
  removalDuration: number;
  /** Pixel movement before canceling and switching to drag */
  movementThreshold: number;
}

/**
 * Callbacks for gesture events
 */
export interface RemovalGestureCallbacks {
  /** Called when hold is detected (start animations) */
  onHoldDetected: (creatureId: string, hex: AxialCoordinate) => void;
  /** Called each frame during removal (update timer circle) */
  onRemovalProgress: (progress: number) => void;
  /** Called when removal completes */
  onRemovalComplete: (creatureId: string, hex: AxialCoordinate) => void;
  /** Called when removal is cancelled */
  onRemovalCancelled: () => void;
  /** Called when movement detected (should start drag instead) */
  onSwitchToDrag: (creatureId: string, hex: AxialCoordinate) => void;
}

const DEFAULT_CONFIG: RemovalGestureConfig = {
  detectionThreshold: 500,   // 500ms before removal sequence starts
  removalDuration: 2500,     // 2500ms for timer to complete (3s total)
  movementThreshold: 20,     // 20px before switching to drag
};

/**
 * Controller for tap-and-hold removal gesture
 */
export class RemovalGestureController {
  private state: RemovalState;
  private config: RemovalGestureConfig;
  private callbacks: RemovalGestureCallbacks;

  private detectionTimer: ReturnType<typeof setTimeout> | null = null;
  private removalAnimationFrame: number | null = null;
  private removalStartTime: number | null = null;

  constructor(callbacks: RemovalGestureCallbacks, config?: Partial<RemovalGestureConfig>) {
    this.callbacks = callbacks;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.state = this.getInitialState();
  }

  private getInitialState(): RemovalState {
    return {
      phase: 'idle',
      targetCreatureId: null,
      targetHex: null,
      pointerStartPos: null,
      holdStartTime: null,
      removalProgress: 0,
    };
  }

  /**
   * Get current state (for external UI)
   */
  getState(): Readonly<RemovalState> {
    return { ...this.state };
  }

  /**
   * Check if currently in removal sequence
   */
  isRemoving(): boolean {
    return this.state.phase === 'removing';
  }

  /**
   * Check if currently detecting hold
   */
  isDetecting(): boolean {
    return this.state.phase === 'detecting';
  }

  /**
   * Start tracking a potential removal gesture
   * Called on pointerdown on a creature
   */
  startTracking(creatureId: string, hex: AxialCoordinate, pointerPos: { x: number; y: number }): void {
    // Clear any existing timers
    this.cleanup();

    this.state = {
      phase: 'detecting',
      targetCreatureId: creatureId,
      targetHex: hex,
      pointerStartPos: pointerPos,
      holdStartTime: Date.now(),
      removalProgress: 0,
    };

    // Start detection timer
    this.detectionTimer = setTimeout(() => {
      this.onHoldDetected();
    }, this.config.detectionThreshold);
  }

  /**
   * Update pointer position during gesture
   * Called on pointermove
   */
  updatePointer(pointerPos: { x: number; y: number }): void {
    if (this.state.phase === 'idle' || this.state.phase === 'cancelled') {
      return;
    }

    if (!this.state.pointerStartPos) {
      return;
    }

    // Calculate movement distance
    const dx = pointerPos.x - this.state.pointerStartPos.x;
    const dy = pointerPos.y - this.state.pointerStartPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If moved too much, cancel and switch to drag
    if (distance > this.config.movementThreshold) {
      this.cancelAndSwitchToDrag();
    }
  }

  /**
   * End the gesture
   * Called on pointerup or pointercancel
   * @returns true if this was a quick tap (should trigger reposition), false otherwise
   */
  endTracking(): boolean {
    if (this.state.phase === 'detecting') {
      // Released before hold detected - this is a quick tap (reposition)
      this.cleanup();
      this.state = this.getInitialState();
      return true; // Signal quick tap
    } else if (this.state.phase === 'removing') {
      // Released during removal sequence - cancel
      this.cancel();
    }
    return false;
  }

  /**
   * Force cancel the gesture
   */
  cancel(): void {
    if (this.state.phase !== 'idle') {
      this.cleanup();
      this.state.phase = 'cancelled';
      this.callbacks.onRemovalCancelled();
      this.state = this.getInitialState();
    }
  }

  /**
   * Called when hold threshold is reached
   */
  private onHoldDetected(): void {
    if (this.state.phase !== 'detecting') {
      return;
    }

    this.state.phase = 'removing';
    this.removalStartTime = Date.now();

    // Notify UI to start animations
    if (this.state.targetCreatureId && this.state.targetHex) {
      this.callbacks.onHoldDetected(this.state.targetCreatureId, this.state.targetHex);
    }

    // Start removal progress animation
    this.startRemovalAnimation();
  }

  /**
   * Animate removal progress
   */
  private startRemovalAnimation(): void {
    const animate = () => {
      if (this.state.phase !== 'removing' || !this.removalStartTime) {
        return;
      }

      const elapsed = Date.now() - this.removalStartTime;
      const progress = Math.min(elapsed / this.config.removalDuration, 1);

      this.state.removalProgress = progress;
      this.callbacks.onRemovalProgress(progress);

      if (progress >= 1) {
        // Removal complete!
        this.onRemovalComplete();
      } else {
        this.removalAnimationFrame = requestAnimationFrame(animate);
      }
    };

    this.removalAnimationFrame = requestAnimationFrame(animate);
  }

  /**
   * Called when removal timer completes
   */
  private onRemovalComplete(): void {
    if (this.state.targetCreatureId && this.state.targetHex) {
      this.callbacks.onRemovalComplete(this.state.targetCreatureId, this.state.targetHex);
    }

    this.cleanup();
    this.state = this.getInitialState();
  }

  /**
   * Cancel and switch to drag mode
   */
  private cancelAndSwitchToDrag(): void {
    const { targetCreatureId, targetHex } = this.state;

    this.cleanup();
    this.state.phase = 'cancelled';

    if (targetCreatureId && targetHex) {
      this.callbacks.onSwitchToDrag(targetCreatureId, targetHex);
    }

    this.state = this.getInitialState();
  }

  /**
   * Clean up timers and animation frames
   */
  private cleanup(): void {
    if (this.detectionTimer) {
      clearTimeout(this.detectionTimer);
      this.detectionTimer = null;
    }

    if (this.removalAnimationFrame) {
      cancelAnimationFrame(this.removalAnimationFrame);
      this.removalAnimationFrame = null;
    }

    this.removalStartTime = null;
  }

  /**
   * Destroy controller
   */
  destroy(): void {
    this.cleanup();
    this.state = this.getInitialState();
  }
}
